import { LAMA_MODULE_BASE, LAMA_ASSESS_SEGMENT } from "@playbookai/types";
import type { KnowledgeScreenContext } from "@playbookai/knowledge-studio-feature";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  PeopleSourceProvider,
  PlayerAILauncher,
  Shell,
  TabStrip,
  Toaster,
  TopBar,
  UniversalSearchDialog,
  useSurfacePresentation,
  toast,
  type ModuleManifest,
  type PlayerAIDraftRequest,
  type PlayerAIMode,
  type PlayerAITabId,
  type SearchResultItem,
  type Tab,
  type TopBarUser,
  type TourCapability,
} from "@playbookai/ui";
import { useTenantPeopleSource } from "../hooks/useTenantPeopleSource.js";
import {
  ShellBridgeProvider,
  type ModuleRailClaimInput,
  type ModuleRailClaimToken,
  type ModuleRailKind,
  type PlayerAIWidgetContext,
  type PlayerAIWidgetRevealOutcome,
  type PlayerAIWorkspacePresence,
  type PlayerAIClientAction,
  type PlayerAIClientSurfaceRegistration,
  type PlayerAIClientSurfaceToken,
  type PlayerAIScopeOverride,
  type RightRailTenant,
  type ShellBridge,
} from "@playbookai/api-clients";
import { useOptionalPlayerAIClient } from "@playbookai/player-ai-client";
import type {
  InteractionContext,
  PlayerAIScope,
} from "@playbookai/player-ai-client";
import type {
  CurrentPageContext,
  PlatformModuleId,
  PlatformNavigationAction,
  PlayerAIOpenDetail,
  PrivatePlayerAIHandoff,
} from "@playbookai/types";
import { setActiveTabLabel, setTabLabel } from "./tab-label-sync.js";
import { ADMIN_TAB_ID, adminTab } from "../admin/admin-tab.js";
import { useAuthStore } from "../auth/auth-store.js";
import { consumeLandingRedirect } from "../pages/IndexRoute.js";
import { settingsApi } from "../api/settings-client.js";
import { launcherPreferencesQueryKey } from "../settings/useSettings.js";
import { canAdministerWorkspace } from "../auth/roles.js";
import { logout } from "../auth/session-api.js";
import {
  ADMIN_ROUTE,
  NOTIFICATIONS_ROUTE,
  SETTINGS_ROUTE,
  TEAMS_ROUTE,
} from "../routes.js";
import { usePresentationTheme } from "../settings/usePresentationTheme.js";
import { useSignInThemeChoice } from "../settings/useSignInThemeChoice.js";
import { useQueryClient } from "@tanstack/react-query";
import {
  useTabs,
  findTabForPath,
  replaceTabsForSession,
  routeForTab,
} from "../store/tabs.js";
import {
  clearTabSession,
  readTabSession,
  tabSessionStorageKey,
  writeTabSession,
  type TabSessionScope,
} from "../store/tab-session.js";
import { ALL_MODULES, getEnabledModules } from "../modules/index.js";
import { moduleUnavailableNotice } from "../modules/availability.js";
import {
  isHomeAtRouteBase,
  isItemIdTabSlot,
  itemLinkFor,
  manifestToTab,
  usesInstanceSegment,
} from "../modules/launcher-entries.js";
import { ModuleLoading } from "../modules/ModuleRouteBoundary.js";
import { PlayerAIRailWired } from "../player-ai/PlayerAIRailWired.js";
import { useWidgetAttachments } from "../player-ai/useWidgetAttachments.js";
import { usePlayerAISendRequest } from "../player-ai/usePlayerAISendRequest.js";
import { NotificationsHost } from "./NotificationsHost.js";
import { notificationHref } from "../notifications/notification-target.js";
import { KnowledgeHelpHost } from "./KnowledgeHelpHost.js";
import {
  ThemeToggleButton,
  ThemeTogglePlaceholder,
} from "./ThemeToggleButton.js";
import { useBellSocket } from "../notifications/useBellSocket.js";
import { useSessionKeepAlive } from "../auth/useSessionKeepAlive.js";
import { getBrandLabel } from "../config/runtime.js";
import {
  SEARCH_SCOPES,
  buildSearchSections,
  platformSearchItemId,
} from "./search-sections.js";
import { usePlatformSearch } from "./usePlatformSearch.js";
import {
  useCurrentPlayerAIContext,
  useCurrentPlayerAIPageContext,
  useCurrentPlayerAIScope,
  mergeCurrentPageInteractionContext,
  sanitizeCurrentPageContext,
} from "../player-ai/scope.js";
import {
  isPlatformNavigationAction,
  resolvePlatformNavigation,
} from "../player-ai/navigation.js";
import { openPrivatePlayerAIHandoff } from "../player-ai/private-handoff.js";
import {
  PLAYER_AI_OPEN_EVENT,
  PRODUCT_NAME,
  type OnboardingTourId,
} from "@playbookai/types";
import { OnboardingTourHost } from "../onboarding/OnboardingTourHost.js";
import { useOnboardingTours } from "../onboarding/useOnboardingTours.js";
import { useOnboardingTourTriggers } from "../onboarding/tour-triggers.js";
import { SETTINGS_TAB_ID, settingsTab } from "../settings/settings-tab.js";
import {
  NOTIFICATIONS_TAB_ID,
  notificationsTab,
} from "../notifications/notifications-tab.js";
import {
  settingsRouteForSearchId,
  settingsSearchItems,
} from "../settings/sections.js";

const PLAYER_AI_MODE_KEY = "playbookai.player-ai.mode";

type ActiveRailOccupant =
  | { readonly kind: "playerAI" }
  | {
      readonly kind: "legacyModule";
      readonly tenant: ModuleRailKind;
    }
  | {
      readonly kind: "moduleClaim";
      readonly token: ModuleRailClaimToken;
    }
  | null;

/** A workspace's published mention roster, with the scope it is for. */
type PublishedPeopleSearch = Readonly<{
  workspaceType: PlayerAIWidgetContext["workspaceType"];
  workspaceId: string;
  resource: NonNullable<PlayerAIWidgetContext["peopleSearchResource"]>;
}>;

function sameModuleRailClaim(
  active: ModuleRailClaimToken,
  candidate: ModuleRailClaimToken,
): boolean {
  return (
    active.claimId === candidate.claimId &&
    active.generation === candidate.generation &&
    active.kind === candidate.kind &&
    active.ownerKey === candidate.ownerKey &&
    active.resourceKey === candidate.resourceKey
  );
}

function readInitialPlayerAiMode(): PlayerAIMode {
  if (typeof window === "undefined") return "rail";
  try {
    const raw = window.localStorage.getItem(PLAYER_AI_MODE_KEY);
    if (raw === "rail" || raw === "popover" || raw === "fullscreen") return raw;
  } catch {
    // Ignore storage failures and use the default.
  }
  return "rail";
}

/**
 * The wiring layer between the data-blind `@playbookai/ui` shell and the
 * app-specific stores / hooks. Every authenticated route renders inside this.
 *
 * Three responsibilities:
 *
 * 1. Wire `TopBar` slots — user identity, dark-mode + logout callbacks, plus
 *    a `TabStrip` that consumes `useTabs`.
 * 2. Sync URL ↔ active tab in both directions (back button, deep links,
 *    programmatic navigation).
 * 3. Auto-open a tab when the route enters a known route (module, launcher,
 *    or Settings) and no matching tab exists. Lets deep links work fresh.
 */

export function AppLayout(): ReactElement {
  // Mounted HERE and only here: the layout renders once per session, so the
  // bell holds exactly one socket. Mounting it inside NotificationsHost would
  // open a second one whenever the popover remounts.
  useBellSocket();
  // Same reasoning, same place: one keep-alive per session. It renews the
  // session cookie while someone is actually present, including when their
  // work is on the canvas and never reaches the BFF by itself.
  useSessionKeepAlive();

  const navigate = useNavigate();
  const location = useLocation();
  // The default landing applies only when the app opens on "/". Opened on any
  // other page, it is spent here, so a later in-app link to "/" shows Home.
  const [entryPath] = useState(() => location.pathname);
  useEffect(() => {
    if (entryPath !== "/") consumeLandingRedirect();
  }, [entryPath]);

  const claims = useAuthStore((s) => s.claims);
  const [knowledgeContext, setKnowledgeContext] =
    useState<KnowledgeScreenContext>();
  const knowledgeContextOwner = useRef<symbol>();
  const publishKnowledgeContext = useCallback(
    (context: KnowledgeScreenContext) => {
      const owner = Symbol("knowledge-screen");
      knowledgeContextOwner.current = owner;
      setKnowledgeContext(context);
      return () => {
        if (knowledgeContextOwner.current !== owner) return;
        knowledgeContextOwner.current = undefined;
        setKnowledgeContext(undefined);
      };
    },
    [],
  );
  const peopleSource = useTenantPeopleSource();
  const {
    darkMode,
    toggleDarkMode,
    read: themeRead,
    setTheme,
  } = usePresentationTheme();
  // AFTER usePresentationTheme on purpose: React runs effects in hook order, so
  // the saved setting is applied first and a theme picked on the sign-in page
  // is then saved over it, before the browser paints (d5).
  useSignInThemeChoice(setTheme);
  // Warm the launcher preference as soon as the session claims exist (the
  // query key needs tenant and user), so IndexRoute's bounded wait for the
  // default landing is normally already answered from cache (PB-5134, t11).
  const queryClient = useQueryClient();
  const claimsTenantId = claims?.tenantId;
  const claimsUserId = claims?.userId;
  useEffect(() => {
    if (!claimsTenantId || !claimsUserId) return;
    void queryClient.prefetchQuery({
      queryKey: launcherPreferencesQueryKey(claimsTenantId, claimsUserId),
      queryFn: () => settingsApi.getLauncherPreferences(),
    });
  }, [claimsTenantId, claimsUserId, queryClient]);

  const tabs = useTabs((s) => s.tabs);
  const activeTabId = useTabs((s) => s.activeTabId);
  const openTab = useTabs((s) => s.openTab);
  const closeTab = useTabs((s) => s.closeTab);
  const forceCloseTab = useTabs((s) => s.forceCloseTab);
  const closeOthers = useTabs((s) => s.closeOthers);
  // The ACTION only — never `lastRouteById` itself. Subscribing to the map
  // would re-render this layout on every navigation; the six "go to that tab"
  // sites read it through `routeForTab`, which uses `useTabs.getState()`.
  const rememberRoute = useTabs((s) => s.rememberRoute);
  const togglePin = useTabs((s) => s.togglePin);
  const setActive = useTabs((s) => s.setActive);

  // The active tab names the page (PB-5175 S2 and the 4.1.3 half of S7): the
  // document title and a persistently mounted polite live region follow its
  // label; the product alone — and silence — when no tab matches (Home).
  // Focus management on route change stays with PB-5175.
  const activeTabLabel = tabs.find((tab) => tab.id === activeTabId)?.label;
  useEffect(() => {
    document.title = activeTabLabel
      ? `${activeTabLabel} · ${PRODUCT_NAME}`
      : PRODUCT_NAME;
  }, [activeTabLabel]);

  const user: TopBarUser = {
    name: claims?.name ?? claims?.email ?? "Signed in",
    initials: deriveInitials(claims?.name ?? claims?.email ?? null),
    ...(claims?.userId ? { id: claims.userId } : {}),
  };

  const enabledModules = useMemo(
    () => getEnabledModules(claims?.enabledModules),
    [claims?.enabledModules],
  );
  // ADR-0100: an Open that reaches a module this shell has not mounted says
  // so, rather than doing nothing.
  const reportModuleUnavailable = useCallback(
    (moduleId: string): void => {
      const label =
        ALL_MODULES.find((m) => m.id === moduleId)?.label ?? moduleId;
      toast(moduleUnavailableNotice(moduleId, label, claims?.enabledModules), {
        tone: "info",
        dedupeKey: `module-unavailable:${moduleId}`,
      });
    },
    [claims?.enabledModules],
  );
  const canAdminister = canAdministerWorkspace(claims);
  const tabSessionScope = useMemo<TabSessionScope | null>(
    () =>
      claimsTenantId && claimsUserId
        ? { tenantId: claimsTenantId, userId: claimsUserId }
        : null,
    [claimsTenantId, claimsUserId],
  );
  const tabSessionScopeKey = tabSessionScope
    ? tabSessionStorageKey(tabSessionScope)
    : null;
  const tabSessionAccess = useMemo(
    () => ({ canAdminister, enabledModules }),
    [canAdminister, enabledModules],
  );
  const tabSessionAccessKey = `${tabSessionScopeKey ?? "anonymous"}|${canAdminister ? "admin" : "member"}|${enabledModules.map((module) => module.id).join(",")}`;
  const [hydratedTabSessionKey, setHydratedTabSessionKey] = useState<
    string | null
  >(null);
  const previousTabSessionRef = useRef<{
    readonly scope: TabSessionScope;
    readonly scopeKey: string;
    readonly accessKey: string;
  } | null>(null);
  const logoutInProgressRef = useRef(false);
  const currentPathRef = useRef(location.pathname);
  currentPathRef.current = location.pathname;

  // Restore before URL synchronisation/bootstrap is allowed to run. The first
  // render still sees Zustand's empty state, so without this gate a deep link
  // could mint a duplicate while its real tab was waiting in sessionStorage.
  useEffect(() => {
    if (!tabSessionScope || !tabSessionScopeKey) {
      setHydratedTabSessionKey(null);
      return;
    }

    const previous = previousTabSessionRef.current;
    if (previous?.scopeKey === tabSessionScopeKey) {
      // Live role changes have their own transition handling below (including
      // routing away from a revoked Admin tab). Do not pre-empt that behavior
      // by pruning the store here; current access is applied on the next hard
      // refresh, and the next checkpoint is filtered immediately.
      previousTabSessionRef.current = {
        scope: tabSessionScope,
        scopeKey: tabSessionScopeKey,
        accessKey: tabSessionAccessKey,
      };
      setHydratedTabSessionKey(tabSessionAccessKey);
      return;
    }
    if (previous && previous.scopeKey !== tabSessionScopeKey) {
      clearTabSession(previous.scope);
    }

    const restored = readTabSession(
      tabSessionScope,
      tabSessionAccess,
      currentPathRef.current,
    );
    if (restored) {
      replaceTabsForSession(restored);
    } else if (previous && previous.scopeKey !== tabSessionScopeKey) {
      // Never carry the prior identity's in-memory tabs into a new scope, even
      // when storage is unavailable or the new scope has no checkpoint.
      replaceTabsForSession({
        tabs: [],
        activeTabId: "",
        counters: {},
        lastRouteById: {},
      });
    }

    previousTabSessionRef.current = {
      scope: tabSessionScope,
      scopeKey: tabSessionScopeKey,
      accessKey: tabSessionAccessKey,
    };
    setHydratedTabSessionKey(tabSessionAccessKey);
  }, [
    tabSessionAccess,
    tabSessionAccessKey,
    tabSessionScope,
    tabSessionScopeKey,
  ]);

  const tabSessionReady =
    tabSessionScope === null || hydratedTabSessionKey === tabSessionAccessKey;

  // Checkpoint after hydration, then after every store mutation. Zustand stays
  // the live source; sessionStorage is only a refresh-scoped recovery record.
  useEffect(() => {
    if (!tabSessionScope || !tabSessionReady) return;
    const persist = (state: ReturnType<typeof useTabs.getState>): void => {
      if (logoutInProgressRef.current) return;
      writeTabSession(tabSessionScope, tabSessionAccess, state);
    };
    persist(useTabs.getState());
    return useTabs.subscribe(persist);
  }, [tabSessionAccess, tabSessionReady, tabSessionScope]);

  // --- Sync direction 1: URL changes → activate the matching tab. -----------
  useEffect(() => {
    if (!tabSessionReady) return;
    const path = location.pathname;
    const match = findTabForPath(tabs, path);
    if (match) {
      if (match.id !== activeTabId) setActive(match.id);
      return;
    }
    // No tab covers this path. Clear active so no pill stays highlighted;
    // bootstrap (below) may still synthesise a tab for known routes.
    if (activeTabId !== "") setActive("");
  }, [location.pathname, tabs, activeTabId, setActive, tabSessionReady]);

  // --- Sync direction 3: URL changes → remember it against its OWNING tab. --
  // The return trip. A tab used to navigate to the route it was CREATED with,
  // so coming back to it discarded the query and any deeper path the user had
  // reached inside it (PB-5295: VSM fell out of `?mode=edit` into Monitor).
  //
  // Attribution is by the tab that OWNS the path, never by `activeTabId`:
  // direction-1 sets that in a LATER commit than the location change, and
  // several navigations move the URL without setting it at all (the Home icon,
  // the ⌘K tab branch, any module-internal absolute navigate). Keying on the
  // active tab would write the new path onto the PREVIOUS one and strand it.
  //
  // A tab may only remember a path it RIGHTFULLY owns. `findTabForPath` falls
  // back to the longest ancestor prefix, so before an entity tab exists its
  // module-home tab owns the entity's URL — and Hoshin's home sits at the bare
  // `/app/hoshin`, an ancestor of every plan. Recording there would make the
  // home pill navigate into a plan for ever. When the module declares a more
  // specific entity route than the owner holds, the rightful owner is a tab
  // that may not exist yet, so nothing is recorded.
  //
  // An item link is about to move to its numbered address (the bootstrap
  // below), so no tab may remember it.
  useEffect(() => {
    if (!tabSessionReady) return;
    const path = location.pathname;
    if (itemLinkFor(path, enabledModules)) return;
    const owner = rightfulTabForPath(tabs, path, enabledModules);
    if (!owner) return;
    rememberRoute(owner.id, `${path}${location.search}`);
  }, [
    location.pathname,
    location.search,
    tabs,
    enabledModules,
    rememberRoute,
    tabSessionReady,
  ]);

  // --- Bootstrap: a deep link into a known route synthesises a tab. ---------
  // Deps intentionally exclude `tabs` — bootstrap only reacts to URL changes,
  // never to its own openTab mutating the tab list. Reading `tabs` via
  // `useTabs.getState()` inside the effect keeps the check correct without
  // re-firing on every tab change (which would race the `navigate` below and
  // spin into an infinite loop).
  //
  // Remembers the bare routeBase we last minted an instance for, so a
  // synchronous double-invoke of this effect (React StrictMode fires effects
  // twice in dev) doesn't mint a duplicate: the first fire mints an id and
  // navigates to `${routeBase}/${id}`, but until that navigation commits the
  // path is still the bare base — so the second fire's `findTabForPath` can't
  // yet see the just-minted tab. Cleared as soon as the path changes (a
  // genuine later navigation), so re-entering the bare base still mints a
  // fresh instance. An item link it rewrites (below) is guarded the same way.
  const mintedBasePathRef = useRef<string | null>(null);
  // A title sent through `setTabLabelForPath` before its entity tab exists —
  // a module navigated straight to an entity whose data was already cached, so
  // the workspace's title effect ran in the same commit as this bootstrap, and
  // child effects run first. Held here so the tab this bootstrap creates is
  // born with it; the workspace will not send the same title again.
  const pendingEntityLabelRef = useRef<{
    route: string;
    label: string;
  } | null>(null);
  useEffect(() => {
    if (!tabSessionReady) return;
    const path = location.pathname;
    if (
      mintedBasePathRef.current !== null &&
      mintedBasePathRef.current !== path
    ) {
      mintedBasePathRef.current = null;
    }
    if (path === "/") return;
    const currentTabs = useTabs.getState().tabs;
    if (
      canAdminister &&
      (path === ADMIN_ROUTE || path.startsWith(`${ADMIN_ROUTE}/`))
    ) {
      const currentAdminTab = currentTabs.find(
        (tab) => tab.id === ADMIN_TAB_ID,
      );
      if (!currentAdminTab || currentAdminTab.route !== path) {
        openTab(adminTab(path));
      }
      return;
    }
    if (path === SETTINGS_ROUTE || path.startsWith(`${SETTINGS_ROUTE}/`)) {
      const currentSettingsTab = currentTabs.find(
        (tab) => tab.id === SETTINGS_TAB_ID,
      );
      if (!currentSettingsTab || currentSettingsTab.route !== path) {
        openTab(settingsTab(path));
      }
      return;
    }
    if (path === NOTIFICATIONS_ROUTE) {
      const currentNotificationsTab = currentTabs.find(
        (tab) => tab.id === NOTIFICATIONS_TAB_ID,
      );
      if (!currentNotificationsTab) openTab(notificationsTab());
      return;
    }
    const module = enabledModules.find(
      (m) => path === m.routeBase || path.startsWith(`${m.routeBase}/`),
    );
    // d1: a SOLVE or VSM tab number comes from the counter, so an item id in
    // that slot (a notification opened in a new tab, a copied link, the Jira
    // origin line, a tab the browser restored from an old link) names the
    // item. Open it under a numbered tab and replace the address. This runs
    // before the open-tab exit below: a restored tab sits at this very address.
    const itemLink = itemLinkFor(path, enabledModules);
    if (itemLink) {
      if (mintedBasePathRef.current === path) return;
      mintedBasePathRef.current = path;
      const own = currentTabs.find(
        (tab) => tab.id === `${itemLink.module.id}-${itemLink.itemId}`,
      );
      // The item's own tab number, else any open tab's number in the module
      // (d7, the Player AI rule), else a new one. Never an item id.
      const route = placeModuleEntityTab(
        itemLink.module,
        itemLink.itemId,
        (own && extractInstanceId(own.route, itemLink.module)) ??
          openTabNumber(currentTabs, itemLink.module),
      );
      navigate(`${route}${itemLink.tail}${location.search}${location.hash}`, {
        replace: true,
      });
      return;
    }
    const entityRoute = module
      ? extractManifestEntityRoute(path, module)
      : undefined;
    const matchingTab = findTabForPath(currentTabs, path);
    // A library tab is an ancestor-prefix match for its entity URLs. That is
    // sufficient for ordinary module sub-routes, but not for a manifest-declared
    // entity route: only the stable entity tab may suppress reconstruction.
    if (
      matchingTab &&
      (!module ||
        !entityRoute ||
        matchingTab.id === `${module.id}-${entityRoute.entityId}`)
    ) {
      return;
    }

    if (module) {
      // Some module routes separate a shell tab instance from the stable
      // entity id (`/app/vsm/:instanceId/:vsmId`). Reconstruct the entity tab
      // before the generic instance bootstrap so a cold deep link does not
      // collapse into a library tab whose route goes stale after Back/delete.
      if (entityRoute) {
        const pending = pendingEntityLabelRef.current;
        pendingEntityLabelRef.current = null;
        const base = manifestToTab(module, entityRoute.entityId);
        openTab({
          ...base,
          id: `${module.id}-${entityRoute.entityId}`,
          label:
            pending?.route === entityRoute.route ? pending.label : base.label,
          route: entityRoute.route,
        });
        return;
      }

      // A module whose home IS its routeBase gets no minted instance: the
      // shell used to invent one, which is what made Hoshin's grid URL
      // indistinguishable from a plan URL in the first place.
      if (isHomeAtRouteBase(module) && path === module.routeBase) {
        openTab(manifestToTab(module, ""));
        return;
      }

      // The URL may already include deeper sub-routes (e.g. `/new`,
      // `/<solveId>/edit`). Only rewrite the URL when we have to *mint* a
      // fresh instance-id — otherwise the deeper tail belongs to the
      // module's nested router and must be left intact.
      if (
        module.id === "lama" &&
        path === `${LAMA_MODULE_BASE}/${LAMA_ASSESS_SEGMENT}`
      ) {
        openTab({
          ...manifestToTab(module, LAMA_ASSESS_SEGMENT),
          label: "My assessment",
        });
        return;
      }
      const explicitInstance = extractInstanceId(path, module);
      if (!explicitInstance) {
        // Bare routeBase mint — dedup the StrictMode double-fire (see above).
        if (mintedBasePathRef.current === path) return;
        mintedBasePathRef.current = path;
      }
      // Same rule the launcher follows: only an `instanceSegment` module
      // consumes a minted id, so the others never bump their counter.
      const instanceId =
        explicitInstance ??
        (usesInstanceSegment(module)
          ? String(useTabs.getState().nextCount(module.id))
          : "");
      const tab = manifestToTab(module, instanceId);
      openTab(tab);
      if (!explicitInstance) {
        // Carry the query across the mint. A cold `/app/lama?tab=campaigns`
        // would otherwise be rewritten to `/app/lama/1` and lose it before the
        // recording effect ever sees a tab to attribute it to.
        navigate(`${tab.route}${location.search}`, { replace: true });
      }
      return;
    }

    if (path.startsWith("/launcher/")) {
      const tabIdSegment = path.slice("/launcher/".length).split("/")[0];
      if (tabIdSegment) {
        openTab({
          id: `launcher-${tabIdSegment}`,
          label: "New tab",
          route: `/launcher/${tabIdSegment}`,
        });
      }
    }
  }, [
    location.pathname,
    location.search,
    location.hash,
    enabledModules,
    openTab,
    navigate,
    canAdminister,
    tabSessionReady,
  ]);

  // A direct URL with no active tab (a disabled-module deep link or an unknown
  // path) is no longer redirected Home — the `path="*"` route in App.tsx renders
  // a safe unavailable / not-found state in place instead (PB-34, SH-05 AC2).

  const onTabClick = (id: string): void => {
    const tab = tabs.find((t) => t.id === id);
    if (!tab) return;
    setActive(id);
    // Uniform for every pill, the active one included: `routeForTab` resolves to
    // the current URL there, so the comparison is equal and the click no-ops.
    const target = routeForTab(id) ?? tab.route;
    if (target !== `${location.pathname}${location.search}`) navigate(target);
  };

  const onAddClick = useCallback((): void => {
    // Each click mints a fresh ephemeral launcher tab. The Launcher page
    // reads `:tabId` from the URL and uses `replaceTab` once the user picks
    // an app, so the same pill morphs into the module tab in place.
    const instanceId = String(useTabs.getState().nextCount("launcher"));
    openTab({
      id: `launcher-${instanceId}`,
      label: "New tab",
      route: `/launcher/${instanceId}`,
    });
    navigate(`/launcher/${instanceId}`);
  }, [navigate, openTab]);

  // Wrap tab removal so the URL follows the new active tab (or Home if none).
  // Without this, the bootstrap effect would re-synthesise the just-closed
  // tab because the URL still points at its route.
  const closeAndFollow = useCallback(
    (id: string, mode: "normal" | "forced"): void => {
      const before = useTabs.getState();
      const target = before.tabs.find((tab) => tab.id === id);
      if (!target) return;
      const wasActive = before.activeTabId === id;

      if (mode === "forced") forceCloseTab(id);
      else closeTab(id);

      const after = useTabs.getState();
      if (after.tabs.some((tab) => tab.id === id) || !wasActive) return;
      if (after.activeTabId === "") {
        if (location.pathname !== "/") navigate("/");
        return;
      }
      const next = after.tabs.find((tab) => tab.id === after.activeTabId);
      if (!next) return;
      const destination = routeForTab(next.id) ?? next.route;
      // A FORCED close always navigates — no equality short-circuit. Once the
      // revoked tab is gone its URL can be prefix-owned by the survivor, so the
      // remembered route can equal the URL we are standing on and the skip would
      // leave the user on the "Access removed" screen of a tab that no longer
      // exists.
      if (mode === "forced") {
        navigate(destination);
        return;
      }
      if (destination !== `${location.pathname}${location.search}`) {
        navigate(destination);
      }
    },
    [closeTab, forceCloseTab, location.pathname, location.search, navigate],
  );

  /* A live `/me` refresh can revoke the administrator role while this layout
     stays mounted. The route gate removes the protected content, but the shell
     still owns the tab and URL: retire both on the granted -> denied edge so a
     revoked user cannot be left with an active Admin console pill. Initial
     non-admin deep links keep the route gate's explanatory denial instead of
     being redirected without context. */
  const couldAdministerRef = useRef(canAdminister);
  useEffect(() => {
    const couldAdminister = couldAdministerRef.current;
    couldAdministerRef.current = canAdminister;
    if (!couldAdminister || canAdminister) return;
    closeAndFollow(ADMIN_TAB_ID, "forced");
  }, [canAdminister, closeAndFollow]);

  const onTabClose = useCallback(
    (id: string): void => closeAndFollow(id, "normal"),
    [closeAndFollow],
  );

  const onTabCloseOthers = useCallback(
    (id: string): void => {
      closeOthers(id);
      const s = useTabs.getState();
      const next = s.tabs.find((t) => t.id === s.activeTabId);
      if (!next) return;
      const destination = routeForTab(next.id) ?? next.route;
      if (destination !== `${location.pathname}${location.search}`) {
        navigate(destination);
      }
    },
    [closeOthers, navigate, location.pathname, location.search],
  );

  // --- Universal search: ⌘K / Ctrl+K opens; sections come from real shell
  // state (open tabs + enabled modules) via the data-blind provider contract.
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Product entities stay in their owning services. The shell sends only the
  // current query; the platform registry fans out to permission-aware owners.
  const platformSearch = usePlatformSearch(searchQuery, searchOpen);

  const searchSections = useMemo(
    () =>
      buildSearchSections(
        tabs,
        enabledModules,
        platformSearch.results,
        settingsSearchItems(claims),
        platformSearch,
      ),
    [tabs, enabledModules, platformSearch, claims],
  );

  // Before the user types, show only tabs + apps (the prototype reveals SOLVE
  // matches on input, and an empty query would otherwise dump up to 100 rows).
  const emptyQuerySections = useMemo(
    () => searchSections.filter((s) => s.id !== "work"),
    [searchSections],
  );

  // Open (or reactivate) a dedicated tab for a SOLVE, keyed by its id and
  // labelled with its title, then navigate to its workspace. This is the
  // prototype's "each SOLVE is its own tab" behaviour. Reuses the SOLVE
  // instance in the current URL when present (so Back returns to that
  // dashboard), otherwise mints one. Injected into the feature via ShellBridge.
  const openSolve = useCallback(
    ({
      solveId,
      title,
      search = "",
    }: {
      solveId: string;
      title: string;
      search?: string;
    }): void => {
      const module = enabledModules.find((m) => m.id === "solve");
      if (!module) {
        reportModuleUnavailable("solve");
        return;
      }
      const instanceId =
        extractInstanceId(location.pathname, module) ??
        String(useTabs.getState().nextCount(module.id));
      const route = `${module.routeBase}/${instanceId}/${solveId}`;
      openTab({
        ...manifestToTab(module, instanceId),
        id: `solve-${solveId}`,
        label: title,
        route,
      });
      navigate(`${route}${search}`);
    },
    [
      enabledModules,
      location.pathname,
      openTab,
      navigate,
      reportModuleUnavailable,
    ],
  );

  const closeSolveTab = useCallback((solveId: string): void => {
    useTabs.getState().closeTab(`solve-${solveId}`);
  }, []);

  // Module-agnostic "open this entity as its own titled tab". Single-segment
  // modules omit instanceId (`${routeBase}/${entityId}`); SOLVE-style modules
  // supply it (`${routeBase}/${instanceId}/${entityId}`). Tab identity remains
  // `${moduleId}-${entityId}` in both shapes.
  const openModuleEntityTab = useCallback(
    ({
      moduleId,
      instanceId,
      entityId,
      title,
      search,
    }: {
      moduleId: string;
      instanceId?: string;
      entityId: string;
      title: string;
      search?: string;
    }): void => {
      const module = enabledModules.find((m) => m.id === moduleId);
      if (!module) {
        reportModuleUnavailable(moduleId);
        return;
      }
      const tabId = `${moduleId}-${entityId}`;
      const existing = useTabs
        .getState()
        .tabs.find((candidate) => candidate.id === tabId);
      // Callers outside a nested module (for example Hoshin Take action)
      // cannot know that module's shell instance. Reuse the existing entity
      // route in that case. An explicit instance remains authoritative and
      // moves the stable entity tab to that instance.
      if (existing && instanceId === undefined) {
        // Keep the open tab's label. `title` is the caller's stand-in (a
        // notification's own title), and a module already showing the entity
        // does not remount to rename the tab again, so it would stick.
        openTab(existing);
        // With no explicit search, return to where the tab last WAS
        // (`routeForTab`, as a pill click does — PB-5295): `tab.route` is
        // never updated by `rememberRoute`, so it drops the remembered query.
        const target =
          search === undefined
            ? reopenRoute(tabId, existing.route)
            : `${pathOnly(existing.route)}${search}`;
        // Already there: a second push would make the next Back look dead.
        if (target !== `${location.pathname}${location.search}`) {
          navigate(target);
        }
        return;
      }
      /* WHICH MODULES CARRY AN INSTANCE SEGMENT is `MODULE_HOME_ROUTE`'s
         question, and `usesInstanceSegment` is the one answer — the same one
         the launcher and the deep-link bootstrap already use.

         This used to infer it from `entityRouteDepth > 1`, which is a DIFFERENT
         question: that field says how to RECOGNISE an entity route in a cold
         deep link, and only VSM happens to answer both with one number. For a
         module that scopes its routes by instance but whose deeper segments are
         SECTIONS rather than entities — LAMA, whose routes are
         `/:instanceId/responses`, `/:instanceId/reports/:campaignId` — there is
         no depth that is right for both: 2 would make the bootstrap truncate a
         report URL to its section. So the two questions are asked separately.

         The bug that surfaced it: a LAMA caller with no instance got
         `/app/lama/responses`, which the module router reads as
         `:instanceId = "responses"` and renders the questionnaire library for a
         Transformation Lead — a member only avoided it because the role
         redirect masked it. SOLVE (`:instanceId/:solveId`, also
         `instanceSegment`, no declared depth) had the same latent defect. */
      const route = placeModuleEntityTab(module, entityId, instanceId, title);
      navigate(`${route}${search ?? ""}`);
    },
    [
      enabledModules,
      location.pathname,
      location.search,
      openTab,
      navigate,
      reportModuleUnavailable,
    ],
  );

  // A notification row's link, for a new browser tab or a copied link: the
  // reference itself, which the bootstrap opens under a numbered tab (d1).
  const resolveNotificationHref = useCallback(
    (href: string, eventType?: string): string =>
      notificationHref(
        href,
        enabledModules.map((module) => module.id),
        eventType,
      ),
    [enabledModules],
  );

  // --- Right-rail arbitration (PB-163, FE-02) ------------------------------
  // A single host-owned tenant decides who occupies the right edge: the Player
  // AI rail (this shell) XOR a module's Audit rail (SolveWorkspace, reached via
  // the ShellBridge below). Opening one evicts the other; closing clears the
  // edge. `autoOpenRail` is a transient "render as rail" flag for the
  // first-SOLVE-entry auto-open (effect further down) — it is NEVER persisted,
  // so it can't clobber the user's saved Player AI mode. `solveAutoOpenedRef`
  // makes that auto-open strictly once-per-session.
  const [activeRailOccupant, setActiveRailOccupantState] =
    useState<ActiveRailOccupant>(null);
  const activeRailOccupantRef = useRef<ActiveRailOccupant>(null);
  const railGenerationRef = useRef(0);
  const [playerAiOpenRevision, setPlayerAiOpenRevision] = useState(0);
  const [autoOpenRail, setAutoOpenRail] = useState(false);
  const solveAutoOpenedRef = useRef(false);
  const [playerAiWorkspacePresence, setPlayerAiWorkspacePresence] =
    useState<PlayerAIWorkspacePresence | null>(null);
  /* PB-4923 AC6 — the module's name/params lookups for Player AI notices. Held
     in a ref rather than state: the resolvers change identity whenever presence
     or the roster does, and the rail reads them only while rendering a notice,
     so storing them in state would re-render the whole layout for a value
     nothing is watching. */
  const playerAiWidgetContextRef = useRef<PlayerAIWidgetContext | undefined>(
    undefined,
  );
  /* Task 6 — the one published value the rail reads while RENDERING rather
     than inside a notice: where the mention picker reads its member roster. It is
     mirrored into state and compared by value, so a workspace arriving or
     leaving re-renders the rail once instead of waiting for an unrelated
     render, while a republish of the same resource (the module's effect re-runs
     whenever a resolver changes identity) changes nothing. */
  const [playerAiPeopleSearch, setPlayerAiPeopleSearch] = useState<
    PublishedPeopleSearch | undefined
  >(undefined);
  // Tri-state: undefined uses the route scope, null forces no scope, and an
  // object forces that scope. Keep null and undefined distinct.
  const [playerAiScopeOverride, setPlayerAiScopeOverride] = useState<
    PlayerAIScopeOverride | null | undefined
  >(undefined);
  const [playerAiClientSurface, setPlayerAiClientSurface] = useState<
    PlayerAIClientSurfaceRegistration | undefined
  >(undefined);
  const [publishedPlayerAiPageContext, setPublishedPlayerAiPageContext] =
    useState<
      Readonly<{ routeKey: string; context: CurrentPageContext }> | undefined
    >(undefined);
  const playerAiClientActionLedgerRef = useRef<Map<string, Set<string>>>(
    new Map(),
  );

  const installRailOccupant = useCallback((next: ActiveRailOccupant): void => {
    activeRailOccupantRef.current = next;
    setActiveRailOccupantState(next);
  }, []);

  const rightRailTenant: RightRailTenant | null =
    activeRailOccupant?.kind === "playerAI"
      ? "playerAI"
      : activeRailOccupant?.kind === "legacyModule"
        ? activeRailOccupant.tenant
        : activeRailOccupant?.kind === "moduleClaim"
          ? activeRailOccupant.token.kind
          : null;

  // Every public occupant mutator owns the autoOpenRail reset, so the transient
  // flag cannot leak past its one intended use (e.g. reopening Player AI after
  // Audit, or an event-driven reopen, lands in the persisted mode — never a
  // stale forced rail). The auto-open effect is the sole exception: it installs
  // PlayerAI + the flag directly, because routing through openRail() would wipe
  // the flag it sets.
  const openRail = useCallback(
    (tenant: RightRailTenant): void => {
      // A new explicit opening resolves its conversation again, even if the
      // preceding popover is still fading out. Reusing that closing mount would
      // skip the fresh scope read required after a SOLVE phase advance.
      if (
        tenant === "playerAI" &&
        activeRailOccupantRef.current?.kind !== "playerAI"
      ) {
        setPlayerAiOpenRevision((revision) => revision + 1);
      }
      setAutoOpenRail(false);
      installRailOccupant(
        tenant === "playerAI"
          ? { kind: "playerAI" }
          : { kind: "legacyModule", tenant },
      );
    },
    [installRailOccupant],
  );

  const closeRail = useCallback(
    (tenant?: RightRailTenant): void => {
      const active = activeRailOccupantRef.current;
      if (active?.kind === "moduleClaim") return;
      const activeTenant =
        active?.kind === "playerAI"
          ? "playerAI"
          : active?.kind === "legacyModule"
            ? active.tenant
            : null;
      if (tenant !== undefined && activeTenant !== tenant) return;
      setAutoOpenRail(false);
      installRailOccupant(null);
    },
    [installRailOccupant],
  );

  const claimModuleRail = useCallback(
    (input: ModuleRailClaimInput): ModuleRailClaimToken => {
      const generation = railGenerationRef.current + 1;
      railGenerationRef.current = generation;
      const token: ModuleRailClaimToken = {
        ...input,
        claimId: `module-rail-${generation}`,
        generation,
      };
      setAutoOpenRail(false);
      installRailOccupant({ kind: "moduleClaim", token });
      return token;
    },
    [installRailOccupant],
  );

  const releaseModuleRail = useCallback(
    (token: ModuleRailClaimToken): void => {
      const active = activeRailOccupantRef.current;
      if (
        active?.kind !== "moduleClaim" ||
        !sameModuleRailClaim(active.token, token)
      ) {
        return;
      }
      setAutoOpenRail(false);
      installRailOccupant(null);
    },
    [installRailOccupant],
  );

  const publishPlayerAIWidgetContext = useCallback(
    (context: PlayerAIWidgetContext | undefined): void => {
      playerAiWidgetContextRef.current = context;
      const resource = context?.peopleSearchResource;
      setPlayerAiPeopleSearch((current) => {
        if (context === undefined || resource === undefined) return undefined;
        return current?.workspaceType === context.workspaceType &&
          current.workspaceId === context.workspaceId &&
          current.resource.resourceType === resource.resourceType &&
          current.resource.resourceId === resource.resourceId &&
          current.resource.roster === resource.roster
          ? current
          : {
              workspaceType: context.workspaceType,
              workspaceId: context.workspaceId,
              resource,
            };
      });
    },
    [],
  );

  const publishPlayerAIWorkspacePresence = useCallback(
    (presence: PlayerAIWorkspacePresence): void => {
      setPlayerAiWorkspacePresence((current) => {
        if (
          presence.status === "unknown" &&
          current !== null &&
          (current.workspaceType !== presence.workspaceType ||
            current.workspaceId !== presence.workspaceId)
        ) {
          return current;
        }
        return presence;
      });
    },
    [],
  );
  const publishPlayerAIScopeOverride = useCallback(
    (scope: PlayerAIScopeOverride | null | undefined): void => {
      setPlayerAiScopeOverride(scope);
    },
    [],
  );
  const publishPlayerAICurrentPageContext = useCallback(
    (context: CurrentPageContext | undefined): void => {
      const sanitized = sanitizeCurrentPageContext(context);
      setPublishedPlayerAiPageContext(
        sanitized
          ? {
              routeKey: `${location.pathname}${location.search}`,
              context: sanitized,
            }
          : undefined,
      );
    },
    [location.pathname, location.search],
  );

  const openPlayerAIEntity = useCallback(
    (action: PlatformNavigationAction): boolean => {
      const destination = resolvePlatformNavigation(
        action,
        enabledModules,
        (moduleId: PlatformModuleId): string => {
          const module = enabledModules.find(
            (candidate) => candidate.id === moduleId,
          );
          if (!module) return "";
          const current = extractInstanceId(location.pathname, module);
          if (current) return current;
          const existing = openTabNumber(useTabs.getState().tabs, module);
          return existing ?? String(useTabs.getState().nextCount(module.id));
        },
      );
      if (!destination) return false;
      const isLamaAssessment =
        destination.module === "lama" &&
        pathOnly(destination.route) ===
          `${LAMA_MODULE_BASE}/${LAMA_ASSESS_SEGMENT}`;
      const tabId = isLamaAssessment
        ? `lama-${LAMA_ASSESS_SEGMENT}`
        : destination.tabId;
      // An entity whose tab is already open goes back to where that tab last
      // WAS (as a pill click, openModuleEntityTab and the palette's tab result
      // do), when the destination names the tab's own route rather than a
      // different or deeper place.
      const existing = destination.opensTab
        ? useTabs.getState().tabs.find((tab) => tab.id === tabId)
        : undefined;
      // Assessment links explicitly select an assignment/question. Ordinary
      // campaign/report links resume their own remembered view and filters.
      const target =
        !isLamaAssessment &&
        existing !== undefined &&
        pathOnly(existing.route) === pathOnly(destination.route)
          ? reopenRoute(tabId, destination.route)
          : destination.route;
      if (destination.opensTab) {
        openTab({
          ...(destination.shellManifest
            ? manifestToTab(
                destination.shellManifest,
                destination.instanceId ?? destination.entityId,
              )
            : {}),
          id: tabId,
          label: destination.label,
          // The assessment tab is ONE pill for every wave, so it is keyed on
          // the bare path and its selectors live in the remembered route. Every
          // other destination keeps its own route whole.
          route: isLamaAssessment
            ? pathOnly(destination.route)
            : destination.route,
        });
      }
      // Already there: a second push would make the next Back look dead.
      if (target !== `${location.pathname}${location.search}`) {
        navigate(target);
      }
      return true;
    },
    [enabledModules, location.pathname, location.search, navigate, openTab],
  );

  const continueInPrivatePlayerAI = useCallback(
    (handoff: PrivatePlayerAIHandoff): void => {
      openPrivatePlayerAIHandoff(handoff);
    },
    [],
  );
  // Single-slot, last-writer-wins — but a release must name the registration it
  // is releasing, so a publisher that unmounts after being superseded cannot
  // clear its successor's surface.
  const playerAiSurfaceRegistrationRef = useRef(0);
  const publishPlayerAIClientSurface = useCallback(
    (
      surface: PlayerAIClientSurfaceRegistration | undefined,
    ): PlayerAIClientSurfaceToken => {
      const registrationId = playerAiSurfaceRegistrationRef.current + 1;
      playerAiSurfaceRegistrationRef.current = registrationId;
      setPlayerAiClientSurface(surface);
      return { registrationId };
    },
    [],
  );
  const releasePlayerAIClientSurface = useCallback(
    (token: PlayerAIClientSurfaceToken): void => {
      if (playerAiSurfaceRegistrationRef.current !== token.registrationId) {
        return;
      }
      setPlayerAiClientSurface(undefined);
    },
    [],
  );

  // Counterpart to openModuleEntityTab: close the `${moduleId}-${entityId}` tab
  // and let onTabClose move the URL to the newly-active tab — so a module's
  // "back to library" doesn't mint a duplicate tab at the bare route base.
  const closeModuleEntityTab = useCallback(
    ({
      moduleId,
      entityId,
      terminalClose,
      terminalClaim,
    }: {
      moduleId: string;
      entityId: string;
      terminalClose?: true;
      terminalClaim?: ModuleRailClaimToken;
    }): void => {
      if (terminalClaim) releaseModuleRail(terminalClaim);
      closeAndFollow(
        `${moduleId}-${entityId}`,
        terminalClose || terminalClaim ? "forced" : "normal",
      );
    },
    [closeAndFollow, releaseModuleRail],
  );

  const [playerAiRefreshRevision, setPlayerAiRefreshRevision] = useState(0);
  const requestPlayerAIConversationRefresh = useCallback((): void => {
    setPlayerAiRefreshRevision((current) => current + 1);
  }, []);

  // Label the tab that OWNS the caller's path, never the active one: a
  // workspace's title effect runs before direction-1 has moved `activeTabId`
  // (see `ShellBridge.setTabLabelForPath`). Reads the live strip so a title
  // that arrives after a tab change still finds its tab. When the rightful
  // owner is an entity tab the bootstrap has yet to create, the title waits
  // for it rather than going to the module home that holds the path by prefix.
  const setTabLabelForPath = useCallback(
    ({ path, label }: { path: string; label: string }): void => {
      const owner = rightfulTabForPath(
        useTabs.getState().tabs,
        path,
        enabledModules,
      );
      if (owner) {
        setTabLabel(owner.id, label);
        return;
      }
      const declared = declaredEntityRoute(path, enabledModules);
      if (declared) {
        pendingEntityLabelRef.current = { route: declared.route, label };
      }
    },
    [enabledModules],
  );

  const shellBridge = useMemo<ShellBridge>(
    () => ({
      publishKnowledgeContext,
      setActiveTabLabel,
      setTabLabelForPath,
      openSolve,
      closeSolveTab,
      openModuleEntityTab,
      closeModuleEntityTab,
      rightRailTenant,
      openRail,
      closeRail,
      claimModuleRail,
      releaseModuleRail,
      publishPlayerAIWorkspacePresence,
      publishPlayerAIWidgetContext,
      requestPlayerAIConversationRefresh,
      publishPlayerAIScopeOverride,
      publishPlayerAICurrentPageContext,
      openPlayerAIEntity,
      continueInPrivatePlayerAI,
      publishPlayerAIClientSurface,
      releasePlayerAIClientSurface,
    }),
    [
      publishKnowledgeContext,
      setTabLabelForPath,
      openSolve,
      closeSolveTab,
      openModuleEntityTab,
      closeModuleEntityTab,
      rightRailTenant,
      openRail,
      closeRail,
      claimModuleRail,
      releaseModuleRail,
      publishPlayerAIWorkspacePresence,
      publishPlayerAIWidgetContext,
      requestPlayerAIConversationRefresh,
      publishPlayerAIScopeOverride,
      publishPlayerAICurrentPageContext,
      openPlayerAIEntity,
      continueInPrivatePlayerAI,
      publishPlayerAIClientSurface,
      releasePlayerAIClientSurface,
    ],
  );

  const onSearchSelect = useCallback(
    (item: SearchResultItem): void => {
      if (item.id.startsWith("tab:")) {
        const tab = useTabs
          .getState()
          .tabs.find((t) => `tab:${t.id}` === item.id);
        if (tab) navigate(routeForTab(tab.id) ?? tab.route);
        return;
      }
      if (item.id.startsWith("app:")) {
        const moduleId = item.id.slice("app:".length);
        const module = enabledModules.find((m) => m.id === moduleId);
        // Navigating to the bare routeBase lets the deep-link bootstrap mint
        // the instance id and synthesise the tab — same path as a deep link.
        if (module) navigate(module.routeBase);
        return;
      }
      const settingsRoute = settingsRouteForSearchId(item.id);
      if (settingsRoute) {
        navigate(settingsRoute);
        return;
      }
      if (item.id.startsWith("entity:")) {
        const result = platformSearch.results.find(
          (candidate) => platformSearchItemId(candidate) === item.id,
        );
        if (!result) return;
        openPlayerAIEntity({
          action_id: "platform.navigate@1",
          kind: "platform.navigate@1",
          contract_version: 1,
          target: result.navigation_target,
          label: result.navigation_label ?? result.title,
        });
      }
    },
    [enabledModules, navigate, openPlayerAIEntity, platformSearch.results],
  );

  const onLogout = (): void => {
    // BFF posture (ADR-0013): the SPA never sees tokens. We POST to
    // /api/auth/logout — the BFF clears the session cookie and returns
    // Keycloak's RP-initiated logout URL (with id_token_hint already
    // attached server-side). Then we redirect the browser to it so
    // Keycloak's SSO cookie is cleared and the user lands at
    // post_logout_redirect_uri. The URL is ALWAYS followed: the BFF returns
    // one even when the app session is already gone, and staying here would
    // leave Keycloak's sign-in alive to sign the person straight back in
    // (PB-5461). `logout()` retries a stale security code once and forgets
    // the tab's kept sign-in page.
    logoutInProgressRef.current = true;
    if (tabSessionScope) clearTabSession(tabSessionScope);
    void logout()
      .then(({ url }) => {
        window.location.assign(url);
      })
      .catch(() => {
        logoutInProgressRef.current = false;
        toast("Could not sign out. Try again.", {
          tone: "critical",
          dedupeKey: "shell:logout",
        });
      });
  };

  // Shell-level Player AI surface (PB-163). The active right-edge tenant lives
  // in `rightRailTenant` (above); here we keep the presentation mode + draft.
  // Rail mode renders in Shell.rightRail; popover and fullscreen remain fixed
  // overlays outside Shell so they do not resize the active page or canvas.
  const [playerAiMode, setPlayerAiModeState] = useState<PlayerAIMode>(
    readInitialPlayerAiMode,
  );
  const [playerAiInitialTab, setPlayerAiInitialTab] =
    useState<PlayerAITabId>("chat");
  const [playerAiDraftRequest, setPlayerAiDraftRequest] = useState<
    PlayerAIDraftRequest | undefined
  >(undefined);
  const playerAiLauncherRef = useRef<HTMLButtonElement | null>(null);
  const pendingLauncherFocusRef = useRef<number | null>(null);
  const [playerAiInteractionContext, setPlayerAiInteractionContext] = useState<
    InteractionContext | undefined
  >(undefined);
  const routePlayerAiScope = useCurrentPlayerAIScope();
  const playerAiScope: PlayerAIScope | null =
    playerAiScopeOverride === undefined
      ? routePlayerAiScope
      : playerAiScopeOverride;
  // A message a feature asked Player AI to post (`send`). Held here, not in
  // the panel, so it survives closing and reopening Player AI.
  const {
    pending: playerAiSendRequest,
    start: startPlayerAiSendRequest,
    claim: claimPlayerAiSendRequest,
    report: reportPlayerAiSendResult,
  } = usePlayerAISendRequest(playerAiScope);
  const playerAiClient = useOptionalPlayerAIClient();
  const observedGuidanceRefresh = useRef(playerAiRefreshRevision);
  useEffect(() => {
    if (observedGuidanceRefresh.current === playerAiRefreshRevision) return;
    observedGuidanceRefresh.current = playerAiRefreshRevision;
    // Confirmed editor saves reach the server even when the chat is closed.
    // The server re-reads authorized objectives and coalesces unchanged revisions.
    if (
      playerAiClient &&
      playerAiScope?.conversationType === "workspace" &&
      playerAiScope.workspaceType === "hoshin_workshop"
    ) {
      let cancelled = false;
      void playerAiClient
        .resolveWorkspaceConversation(
          "hoshin_workshop",
          playerAiScope.workspaceId,
        )
        .then(({ conversation }) => {
          if (!cancelled)
            return playerAiClient.hydrateConversation(conversation.id);
        })
        .catch(() => undefined); // Opening/refresh retries best-effort guidance.
      return () => {
        cancelled = true;
      };
    }
  }, [playerAiClient, playerAiRefreshRevision, playerAiScope]);
  const playerAiContext = useCurrentPlayerAIContext();
  const routePlayerAiPageContext = useCurrentPlayerAIPageContext();
  const currentPlayerAiRouteKey = `${location.pathname}${location.search}`;
  const playerAiPageContext =
    publishedPlayerAiPageContext?.routeKey === currentPlayerAiRouteKey
      ? publishedPlayerAiPageContext.context
      : routePlayerAiPageContext;
  const activePlayerAiWorkspacePresence =
    playerAiScope?.conversationType === "workspace" &&
    playerAiWorkspacePresence?.workspaceType === playerAiScope.workspaceType &&
    playerAiWorkspacePresence.workspaceId === playerAiScope.workspaceId
      ? playerAiWorkspacePresence
      : null;
  /* PB-4923 AC6 — the module's lookups, gated by the SAME scope match presence
     uses. A registration from a workspace the reader has navigated away from
     must never answer for the one they are looking at, so the gate is on every
     call rather than only at publish time: the ref can outlive a scope change
     by a render. `null` from either resolver is the honest answer and the copy
     falls back to "Someone else" / the field's own name. */
  const playerAiWidgetLookups = useMemo(() => {
    const matches = (): PlayerAIWidgetContext | undefined => {
      const context = playerAiWidgetContextRef.current;
      if (context === undefined || playerAiScope === null) return undefined;
      /* Match on the WORKSPACE, not the conversation kind. VSM and the Hoshin
         workshop reach Player AI through a contextual-PRIVATE thread, so
         requiring `conversationType === "workspace"` would have silently
         excluded both modules from ever naming anyone. A root-private scope
         carries no workspace and correctly matches nothing. */
      if (!("workspaceId" in playerAiScope)) return undefined;
      return context.workspaceType === playerAiScope.workspaceType &&
        context.workspaceId === playerAiScope.workspaceId
        ? context
        : undefined;
    };
    /* Task 6 — where the mention picker reads its member roster, through the same
       gate but from state (see `playerAiPeopleSearch`): another workspace's
       members must never be offered here. */
    const peopleSearchResource =
      playerAiPeopleSearch !== undefined &&
      playerAiScope !== null &&
      "workspaceId" in playerAiScope &&
      playerAiPeopleSearch.workspaceType === playerAiScope.workspaceType &&
      playerAiPeopleSearch.workspaceId === playerAiScope.workspaceId
        ? playerAiPeopleSearch.resource
        : undefined;
    return {
      nameForActor: (userId: string): string | null =>
        matches()?.nameForActor(userId) ?? null,
      widgetParams: (widgetId: string): unknown =>
        matches()?.widgetParams(widgetId),
      /* Task 9 — through the SAME scope gate as the two lookups above. A rail
         showing a SOLVE's conversation must never move another workspace's
         canvas, and a module that publishes no canvas (or has navigated away)
         answers "unavailable" rather than nothing, so the row can say so. */
      revealWidget: (widgetId: string): PlayerAIWidgetRevealOutcome =>
        matches()?.revealWidget?.(widgetId) ?? { status: "unavailable" },
      peopleSearchResource: (): PlayerAIWidgetContext["peopleSearchResource"] =>
        peopleSearchResource,
    };
  }, [playerAiPeopleSearch, playerAiScope]);

  const activePlayerAiClientSurface =
    playerAiScope !== null &&
    playerAiClientSurface !== undefined &&
    playerAiScope.conversationType === playerAiClientSurface.conversationType &&
    "workspaceType" in playerAiScope &&
    "workspaceId" in playerAiScope &&
    playerAiScope.workspaceType === playerAiClientSurface.workspaceType &&
    playerAiScope.workspaceId === playerAiClientSurface.workspaceId
      ? playerAiClientSurface
      : undefined;
  const effectivePlayerAiInteractionContext = useMemo<
    InteractionContext | undefined
  >(
    () =>
      mergeCurrentPageInteractionContext(
        activePlayerAiClientSurface
          ? (activePlayerAiClientSurface.interactionContext as InteractionContext)
          : playerAiInteractionContext,
        playerAiPageContext,
      ),
    [
      activePlayerAiClientSurface,
      playerAiInteractionContext,
      playerAiPageContext,
    ],
  );
  const capturePlayerAiInteractionContext = useCallback(():
    | InteractionContext
    | undefined => {
    const context = mergeCurrentPageInteractionContext(
      (activePlayerAiClientSurface?.captureInteractionContext?.() as
        | InteractionContext
        | undefined) ?? effectivePlayerAiInteractionContext,
      playerAiPageContext,
    );
    return context
      ? { ...context, current_page_captured_at: new Date().toISOString() }
      : undefined;
  }, [
    activePlayerAiClientSurface,
    effectivePlayerAiInteractionContext,
    playerAiPageContext,
  ]);
  const applyPlayerAiClientAction = useCallback(
    (action: PlayerAIClientAction): boolean => {
      if (isPlatformNavigationAction(action)) {
        return openPlayerAIEntity(action);
      }
      return activePlayerAiClientSurface?.applyAction(action) ?? false;
    },
    [activePlayerAiClientSurface, openPlayerAIEntity],
  );

  const playerAiSolveWorkspaceId =
    playerAiScope?.conversationType === "workspace" &&
    playerAiScope.workspaceType === "solve"
      ? playerAiScope.workspaceId
      : undefined;
  useEffect(() => {
    // The SOLVE-shaped fallback context applies only to a SOLVE scope. Other
    // modules publish their own surface through the shell bridge; stamping a
    // Hoshin plan or VSM id into a `solve_canvas` ScopeRef mislabels the
    // surface for lens selection server-side.
    setPlayerAiInteractionContext(
      playerAiSolveWorkspaceId !== undefined
        ? interactionContextFromDetail({ solveId: playerAiSolveWorkspaceId })
        : undefined,
    );
  }, [playerAiSolveWorkspaceId]);

  const widgetAttachments = useWidgetAttachments(playerAiSolveWorkspaceId);

  const focusLauncherAfterClose = useCallback((): void => {
    if (pendingLauncherFocusRef.current !== null) {
      window.cancelAnimationFrame(pendingLauncherFocusRef.current);
    }
    pendingLauncherFocusRef.current = window.requestAnimationFrame(() => {
      pendingLauncherFocusRef.current = null;
      playerAiLauncherRef.current?.focus();
    });
  }, []);

  useEffect(
    () => () => {
      if (pendingLauncherFocusRef.current !== null) {
        window.cancelAnimationFrame(pendingLauncherFocusRef.current);
      }
    },
    [],
  );

  // The mode the surface is asked to render in. During the transient first-entry
  // auto-open we force "rail" via `autoOpenRail` without touching the persisted
  // `playerAiMode`, so a saved popover/fullscreen preference survives. The
  // presentation keeps the outgoing mode on screen until its exit finishes.
  const playerAiPresentation = useSurfacePresentation(
    autoOpenRail ? "rail" : playerAiMode,
    rightRailTenant === "playerAI",
    // A module taking the edge evicts Player AI immediately. Never reserve
    // two rails while an outgoing Player AI animation finishes.
    rightRailTenant !== null && rightRailTenant !== "playerAI",
  );
  const effectiveMode = playerAiPresentation.presentation;

  useEffect(() => {
    try {
      window.localStorage.setItem(PLAYER_AI_MODE_KEY, playerAiMode);
    } catch {
      // Ignore storage failures; mode still works for the current session.
    }
  }, [playerAiMode]);

  // Explicit mode change: persist (via the effect above) and drop the transient
  // auto-open rail so the chosen mode takes over rendering. Reachable only while
  // the Player AI rail is open, so it never needs to touch `rightRailTenant`.
  const setPlayerAiMode = useCallback((mode: PlayerAIMode): void => {
    setPlayerAiModeState(mode);
    setAutoOpenRail(false);
  }, []);

  const togglePlayerAiFromLauncher = useCallback(
    (tab: PlayerAITabId = "chat"): void => {
      setPlayerAiInitialTab(tab);
      // Retain the idempotent popover-toggle branch for callers that invoke the
      // callback directly; the contained launcher itself is hidden while open.
      if (playerAiMode === "popover" && rightRailTenant === "playerAI") {
        closeRail("playerAI");
        focusLauncherAfterClose();
        return;
      }
      openRail("playerAI");
    },
    [
      playerAiMode,
      rightRailTenant,
      openRail,
      closeRail,
      focusLauncherAfterClose,
    ],
  );

  const closeOverlayPlayerAi = useCallback((): void => {
    closeRail("playerAI");
    focusLauncherAfterClose();
  }, [closeRail, focusLauncherAfterClose]);

  // Auto-open the Player AI rail the first time the user enters a SOLVE
  // workspace this session (FE-I2). Shown transiently as a rail via
  // `autoOpenRail`; we set the tenant + flag DIRECTLY (not via openRail, which
  // would clear the flag) and never call setPlayerAiMode, so a saved
  // popover/fullscreen preference is preserved. `solveAutoOpenedRef` makes it
  // strictly once-per-session — dismissal or re-entry never re-triggers it.
  const isSolveScope =
    playerAiScope?.conversationType === "workspace" &&
    playerAiScope.workspaceType === "solve";
  useEffect(() => {
    if (isSolveScope && !solveAutoOpenedRef.current) {
      solveAutoOpenedRef.current = true;
      setPlayerAiInitialTab("chat");
      setAutoOpenRail(true);
      installRailOccupant({ kind: "playerAI" });
    }
  }, [installRailOccupant, isSolveScope]);

  useEffect(() => {
    const onOpen = (event: Event): void => {
      const detail = (event as CustomEvent<PlayerAIOpenDetail>).detail;
      // Open Player AI in the user's persisted mode — no forced rail, so an
      // event-driven ask (canvas widget, Home askPlayerAI) doesn't clobber a
      // popover/fullscreen preference.
      openRail("playerAI");
      setPlayerAiInitialTab("chat");
      // Prompt-only entry points keep their existing behavior. Widget asks
      // use the separate attachment queue and never insert prompt text.
      if (detail?.send === true) {
        // Posted on its own by the panel; the composer is never touched, so
        // the person's own draft stays as they left it.
        startPlayerAiSendRequest(detail);
      } else if (detail?.widgetId && detail.widgetKind) {
        // Widget asks attach context only; never seed or replace typed text.
        setPlayerAiDraftRequest(undefined);
      } else if (detail?.prompt) {
        setPlayerAiDraftRequest({
          id: `${Date.now()}-${detail.widgetKind ?? "home"}`,
          text: detail.prompt,
        });
      }
      setPlayerAiInteractionContext(interactionContextFromDetail(detail));
    };
    const onContext = (event: Event): void => {
      const detail = (event as CustomEvent<PlayerAIOpenDetail>).detail;
      setPlayerAiInteractionContext(interactionContextFromDetail(detail));
    };
    window.addEventListener(PLAYER_AI_OPEN_EVENT, onOpen);
    window.addEventListener("playerai:context", onContext);
    return () => {
      window.removeEventListener(PLAYER_AI_OPEN_EVENT, onOpen);
      window.removeEventListener("playerai:context", onContext);
    };
  }, [openRail, startPlayerAiSendRequest]);

  /* First-time onboarding tours. Progress is Auth's (ADR-0021); the shell owns
     composition — which tour may run, what the page can currently honour, and
     the one renderer mounted below. Feature packages announce occasions on a
     window event and never import any of this. */
  const onboardingTours = useOnboardingTours(location.key);
  const openPlayerAIForTour = useCallback(() => {
    if (rightRailTenant === "playerAI") return;
    setPlayerAiInitialTab("chat");
    openRail("playerAI");
  }, [rightRailTenant, openRail]);
  const shellTourCapabilities = useMemo<TourCapability[]>(() => {
    const capabilities: TourCapability[] = [
      // Player AI's launcher is chrome the shell always mounts, so the
      // capability is constant here. What actually removes the step is the
      // TARGET being absent — a rail the user has hidden, a surface that
      // suppresses the launcher — which the step's own missing-target policy
      // already handles. Declaring it keeps the definition honest about what
      // the step depends on rather than pretending it depends on nothing.
      "playerAiEnabled",
    ];
    // Read from the same enabled-module set the launcher and tab strip use, so
    // a tour can never offer a module this deployment has switched off.
    if (enabledModules.some((module) => module.id === "knowledge_studio")) {
      capabilities.push("knowledgeStudioEnabled");
    }
    return capabilities;
  }, [enabledModules]);
  const { capabilities: tourCapabilities } = useOnboardingTourTriggers({
    tours: onboardingTours,
    onHome: location.pathname === "/",
    shellCapabilities: shellTourCapabilities,
  });
  /**
   * Which tour Replay would run for the page currently on screen.
   *
   * Derived from the ROUTE rather than from what was last triggered, so the
   * menu offers the tour for what the reader is looking at now. `null` on a
   * page with no tour, and while one is already running — the renderer refuses
   * a second anyway, and an item that does nothing is worse than no item.
   *
   * A SOLVE workspace offers the OWNER tour: replay is a deliberate request to
   * see the workspace explained, and the collaborator and inviter tours are
   * about situations rather than places.
   */
  const replayableTour = useMemo<OnboardingTourId | null>(() => {
    if (onboardingTours.active !== null) return null;
    const path = location.pathname;
    if (path === "/") return "home";
    if (/^\/app\/solve\/[^/]+\/(?!new(?:\/|$))[^/]+(?:\/edit)?\/?$/.test(path))
      return "solve-owner";
    if (/^\/app\/vsm\/[^/]+\/[^/]+\/?$/.test(path)) return "vsm";
    if (/^\/app\/hoshin\/(?!new(?:\/|$))[^/]+\/?$/.test(path)) return "hoshin";
    return null;
  }, [location.pathname, onboardingTours.active]);

  const pendingItemLink = itemLinkFor(location.pathname, enabledModules);

  return (
    <>
      <Shell
        rightRailOccupied={
          playerAiPresentation.present && effectiveMode === "rail"
        }
        skipLinkLabel="Skip to main content"
        topBar={
          <TopBar
            user={user}
            brandLabel={getBrandLabel(claims?.tenantName ?? null)}
            {...(claims?.tenantShortName
              ? { brandMark: claims.tenantShortName }
              : {})}
            // d9: the theme control is host content in the bar. It mounts once
            // the initial preference read has settled — never on `status`,
            // which is pending during a write and would unmount the button on
            // its own click — holds its 32px slot while the first read is
            // pending, and is absent when the read is unavailable (Settings ›
            // Account keeps the three-way control as the fallback).
            themeToggleSlot={
              themeRead === "ready" ? (
                <ThemeToggleButton
                  darkMode={darkMode}
                  onToggle={toggleDarkMode}
                />
              ) : themeRead === "pending" ? (
                <ThemeTogglePlaceholder />
              ) : undefined
            }
            playerAiSlot={
              enabledModules.some(
                (module) => module.id === "knowledge_studio",
              ) ? (
                <KnowledgeHelpHost
                  canViewAdmin={canAdminister}
                  pathname={location.pathname}
                  {...(knowledgeContext ? { context: knowledgeContext } : {})}
                />
              ) : undefined
            }
            onHome={() => navigate("/")}
            homeActive={location.pathname === "/"}
            onCmdK={() => setSearchOpen(true)}
            // Settings is personal and available to every role. Administrators
            // additionally receive Admin console immediately above it.
            onSettings={() => navigate(SETTINGS_ROUTE)}
            onTeams={() => navigate(TEAMS_ROUTE)}
            {...(canAdminister ? { onAdmin: () => navigate(ADMIN_ROUTE) } : {})}
            {...(replayableTour !== null
              ? /* Offered only where a tour exists for what is on screen, and
                   only while none is already running — a menu item that closes
                   the menu without doing anything is worse than no item.
                   Replaying never resets the stored completion. */
                { onReplayTour: () => onboardingTours.replay(replayableTour) }
              : {})}
            onLogout={onLogout}
            notificationsSlot={
              // PB-1583: the tray renders here, OUTSIDE `ShellBridgeProvider`
              // (which wraps the routed outlet below), so it cannot read the
              // bridge — the entity-tab opener is handed to it directly.
              <NotificationsHost
                openModuleEntityTab={openModuleEntityTab}
                resolveHref={resolveNotificationHref}
              />
            }
            tabsSlot={
              <TabStrip
                tabs={tabs}
                activeTabId={activeTabId}
                onTabClick={onTabClick}
                onTabClose={onTabClose}
                onTabCloseOthers={onTabCloseOthers}
                onTabTogglePin={togglePin}
                onAddClick={onAddClick}
              />
            }
          />
        }
        rightRail={
          playerAiPresentation.present ? (
            <PlayerAIRailWired
              key={playerAiOpenRevision}
              open={playerAiPresentation.open}
              onExited={playerAiPresentation.onExited}
              widgetAttachments={widgetAttachments.pending}
              onConsumeWidgetAttachments={widgetAttachments.consume}
              widgetAttachmentNotice={widgetAttachments.notice}
              clientActionLedger={playerAiClientActionLedgerRef}
              refreshRevision={playerAiRefreshRevision}
              scope={playerAiScope}
              workspacePresence={activePlayerAiWorkspacePresence}
              widgetLookups={playerAiWidgetLookups}
              {...(playerAiContext !== undefined
                ? { context: playerAiContext }
                : {})}
              variant="expanded"
              initialTab={playerAiInitialTab}
              mode={effectiveMode}
              initialFocus={autoOpenRail ? "none" : "composer"}
              {...(playerAiDraftRequest !== undefined
                ? { draftRequest: playerAiDraftRequest }
                : {})}
              {...(playerAiSendRequest !== undefined
                ? { sendRequest: playerAiSendRequest }
                : {})}
              claimSendRequest={claimPlayerAiSendRequest}
              reportSendResult={reportPlayerAiSendResult}
              {...(effectivePlayerAiInteractionContext !== undefined
                ? { interactionContext: effectivePlayerAiInteractionContext }
                : {})}
              captureInteractionContext={capturePlayerAiInteractionContext}
              onClientAction={applyPlayerAiClientAction}
              onSetMode={setPlayerAiMode}
              onToggle={closeOverlayPlayerAi}
            />
          ) : undefined
        }
      >
        <ShellBridgeProvider value={shellBridge}>
          {/* The default PeopleSource (assign-person consistency), mounted at
              the composition root per ADR-0009 so no widget imports an auth
              client itself: the tenant directory with no access list, so a
              person field outside any canvas searches the workspace and
              grants nothing. A canvas wraps its own source over this one. */}
          <PeopleSourceProvider source={peopleSource}>
            {/* An item link shows the module loading until the bootstrap
                moves it to its numbered address: never the list, never blank. */}
            {pendingItemLink ? (
              <ModuleLoading label={pendingItemLink.module.label} />
            ) : (
              <Outlet />
            )}
          </PeopleSourceProvider>
        </ShellBridgeProvider>
      </Shell>
      {/* Route-change announcement (PB-5175 S7, the 4.1.3 half): mounted
          persistently so assistive tech registers the region before its text
          changes; empty — silent — while no tab is active. A live region
          without `role="status"` on purpose: a page's own status message
          (Settings "Saved for this session", a reconnecting banner) must stay
          the one status in the document. */}
      <div
        aria-live="polite"
        aria-atomic="true"
        data-testid="route-announcer"
        className="sr-only"
      >
        {activeTabLabel ?? ""}
      </div>
      {/*
        These overlays are siblings of `</Shell>`, so they do NOT inherit
        `Shell.dir` — `Shell` applies `dir`/`lang` to its own root element only.
        Their logical `end-*` placement therefore resolves against the document
        direction. That is latent rather than live: nothing here passes `dir`, so
        production is always `Shell`'s `ltr` default. Whoever wires RTL must put
        this overlay host under the same direction root, pass `dir`/`lang`
        through explicitly, or make the document root the single authority —
        Storybook sets `document.documentElement.dir`, which masks it in stories.
      */}
      <PlayerAILauncher
        paneOpen={rightRailTenant === "playerAI"}
        buttonRef={playerAiLauncherRef}
        onOpenPane={togglePlayerAiFromLauncher}
      />
      {/* The ONE tour renderer for the whole shell, a sibling of `</Shell>` so
          its scrim covers the chrome as well as the page. Which tour runs is
          decided by `useOnboardingTourTriggers` below; this only draws it. */}
      <OnboardingTourHost
        tours={onboardingTours}
        onOpenPlayerAI={openPlayerAIForTour}
        capabilities={tourCapabilities}
      />
      {/* Single app-root toast host — the DS toast store is module-global, so
          exactly ONE <Toaster/> must be mounted (feature pages must not each
          mount their own, which would double-render every toast). */}
      <Toaster />
      <UniversalSearchDialog
        open={searchOpen}
        onClose={() => {
          setSearchOpen(false);
          setSearchQuery("");
        }}
        sections={searchSections}
        emptyQuerySections={emptyQuerySections}
        scopes={SEARCH_SCOPES}
        onSelect={onSearchSelect}
        onQueryChange={setSearchQuery}
        placeholder="Search tabs and apps…"
      />
    </>
  );
}

function interactionContextFromDetail(
  detail: PlayerAIOpenDetail | undefined,
): InteractionContext | undefined {
  if (!detail?.solveId) return undefined;
  const scopeRef = {
    module_id: "solve",
    surface_type: "solve_canvas",
    surface_id: detail.solveId,
  };
  return {
    active_surface_ref: scopeRef,
    ...(detail.widgetId && detail.widgetKind
      ? {
          selection_refs: [
            {
              type: "widget" as const,
              scope_ref: scopeRef,
              widget_id: detail.widgetId,
              widget_kind: detail.widgetKind,
            },
          ],
        }
      : { selection_refs: [] }),
  };
}

/**
 * Find the tab whose route covers the current path. Exact match first, then
 * a prefix match so module-internal sub-routes (e.g. `/app/solve/<uuid>/phase-2`)
 * still activate the parent tab.
 */

/**
 * Pull the first path segment after `routeBase` (the instance id). Returns
 * undefined when the path is exactly `routeBase` with no trailing segment —
 * the caller mints a fresh id in that case. An item id in a SOLVE or VSM tab
 * slot is an item link, never a tab number (d1), so it is undefined too.
 */
function extractInstanceId(
  path: string,
  module: ModuleManifest,
): string | undefined {
  const { routeBase } = module;
  if (!path.startsWith(`${routeBase}/`)) return undefined;
  const rest = path.slice(routeBase.length + 1);
  const segment = rest.split(/[/?#]/)[0];
  if (routeBase === LAMA_MODULE_BASE && segment === LAMA_ASSESS_SEGMENT)
    return undefined;
  if (segment && isItemIdTabSlot(module, segment)) return undefined;
  return segment && segment.length > 0 ? segment : undefined;
}

/** The tab number of any open tab of this module, first in the strip. */
function openTabNumber(
  tabs: readonly Tab[],
  module: ModuleManifest,
): string | undefined {
  return tabs
    .map((tab) => extractInstanceId(tab.route, module))
    .find((instanceId) => instanceId !== undefined);
}

/**
 * Create the `<module>-<entity>` tab, or move it to `instanceId`, and return
 * its route for the caller to navigate. One function for `openModuleEntityTab`
 * and the bootstrap's item links, so the two cannot place a tab differently. A
 * module that carries an instance segment gets a new number when none is
 * given. A moved tab keeps its label unless the caller names one: a tab the
 * browser restored from an old link keeps the name its workspace gave it.
 */
function placeModuleEntityTab(
  module: ModuleManifest,
  entityId: string,
  instanceId: string | undefined,
  label?: string,
): string {
  const state = useTabs.getState();
  const tabId = `${module.id}-${entityId}`;
  const resolvedInstanceId =
    instanceId ??
    (usesInstanceSegment(module)
      ? String(state.nextCount(module.id))
      : undefined);
  const route = resolvedInstanceId
    ? `${module.routeBase}/${resolvedInstanceId}/${entityId}`
    : `${module.routeBase}/${entityId}`;
  const base = manifestToTab(module, entityId);
  state.openTab({
    ...base,
    id: tabId,
    label:
      label ?? state.tabs.find((tab) => tab.id === tabId)?.label ?? base.label,
    route,
  });
  return route;
}

function pathOnly(route: string): string {
  return route.split(/[?#]/)[0] ?? route;
}

/**
 * Where an already-open entity tab reopens: its remembered route (PB-5295),
 * but only as the QUERY on the requested path. A deeper or redirect-only
 * sub-route the tab once recorded — Hoshin's legacy `/:planId/tti/:itemId`,
 * which redirects to another entity's page — is not where "open this plan"
 * should land.
 */
function reopenRoute(tabId: string, route: string): string {
  const remembered = routeForTab(tabId);
  return remembered !== undefined && pathOnly(remembered) === pathOnly(route)
    ? remembered
    : route;
}

/**
 * The tab that RIGHTFULLY owns `path`: `findTabForPath`'s owner, unless the
 * path's module declares a more specific entity route than that owner holds.
 * Then the rightful owner is an entity tab that does not exist yet — before it
 * does, a module home (Hoshin's at the bare `/app/hoshin`, a VSM Library at
 * `/app/vsm/<instanceId>`) owns the entity's URL by prefix — and the answer is
 * none. One rule for the remembered route and for `setTabLabelForPath`, so a
 * home pill neither remembers an entity's path nor takes its name.
 */
function rightfulTabForPath(
  tabs: readonly Tab[],
  path: string,
  modules: readonly ModuleManifest[],
): Tab | undefined {
  const owner = findTabForPath(tabs, path);
  if (!owner) return undefined;
  const declared = declaredEntityRoute(path, modules);
  return declared && owner.route !== declared.route ? undefined : owner;
}

/** The entity route `path` falls in, when its module declares one. */
function declaredEntityRoute(
  path: string,
  modules: readonly ModuleManifest[],
): { entityId: string; route: string } | undefined {
  const module = modules.find(
    (m) => path === m.routeBase || path.startsWith(`${m.routeBase}/`),
  );
  return module ? extractManifestEntityRoute(path, module) : undefined;
}

function extractManifestEntityRoute(
  path: string,
  module: {
    readonly id: string;
    readonly routeBase: string;
    readonly entityRouteDepth?: number;
  },
): { entityId: string; route: string } | undefined {
  const depth = module.entityRouteDepth;
  if (!depth || depth < 1 || !path.startsWith(`${module.routeBase}/`)) {
    return undefined;
  }
  const segments = path
    .slice(module.routeBase.length + 1)
    .split("/")
    .filter(Boolean);
  if (segments.length < depth) return undefined;
  const entitySegments = segments.slice(0, depth);
  const entityId = entitySegments.at(-1);
  if (!entityId) return undefined;
  return {
    entityId,
    route: `${module.routeBase}/${entitySegments.join("/")}`,
  };
}

/**
 * Derive 1-2 letter initials from a display name or email — used when the
 * user has no avatar image. Defensive against missing / single-word names.
 */
function deriveInitials(source: string | null): string {
  if (!source) return "·";
  const cleaned = source.includes("@")
    ? (source.split("@")[0] ?? source)
    : source;
  const parts = cleaned
    .split(/[\s._-]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return "·";
  if (parts.length === 1) {
    return (parts[0]?.slice(0, 2) ?? "·").toUpperCase();
  }
  const first = parts[0]?.[0] ?? "";
  const last = parts[parts.length - 1]?.[0] ?? "";
  return (first + last).toUpperCase();
}
